import React, { useState, useEffect } from 'react';
import axios from 'axios';

function CallsTableView({ apiBase, loading, setLoading, error, setError }) {
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  
  // Set default dates: today and tomorrow
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const [filters, setFilters] = useState({
    willingToPay: 'all',
    startDate: today.toISOString().split('T')[0],
    endDate: tomorrow.toISOString().split('T')[0],
    searchTerm: ''
  });

  useEffect(() => {
    loadCalls();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [calls, filters]);

  const loadCalls = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBase}/call`);
      setCalls(response.data.data || []);
    } catch (err) {
      setError('Failed to load calls: ' + err.message);
      console.error('Error loading calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...calls];

    // Filter by willing to pay
    if (filters.willingToPay !== 'all') {
      const willingToPay = filters.willingToPay === 'yes';
      filtered = filtered.filter(call => 
        call.call_summary?.willing_to_pay === willingToPay
      );
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(call => {
        const callDate = new Date(call.call_summary?.created_at || call.created_at);
        return callDate >= new Date(filters.startDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(call => {
        const callDate = new Date(call.call_summary?.created_at || call.created_at);
        return callDate <= new Date(filters.endDate);
      });
    }

    // Filter by search term (name, phone, summary)
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(call => {
        const summary = call.call_summary;
        return (
          summary?.first_name?.toLowerCase().includes(term) ||
          summary?.last_name?.toLowerCase().includes(term) ||
          summary?.phone_number?.includes(term) ||
          summary?.summary?.toLowerCase().includes(term) ||
          summary?.client_name?.toLowerCase().includes(term) ||
          summary?.batch_call_name?.toLowerCase().includes(term)
        );
      });
    }

    setFilteredCalls(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const exportToCSV = () => {
    const headers = [
      'Date',
      'First Name',
      'Last Name',
      'Phone Number',
      'Client',
      'Batch',
      'Willing to Pay',
      'User Responded',
      'Duration',
      'Is Voicemail',
      'Cost',
      'Summary'
    ];

    const rows = filteredCalls.map(call => {
      const s = call.call_summary || {};
      return [
        formatDate(s.created_at),
        s.first_name || '',
        s.last_name || '',
        s.phone_number || '',
        s.client_name || '',
        s.batch_call_name || '',
        s.willing_to_pay ? 'Yes' : 'No',
        s.user_responded ? 'Yes' : 'No',
        s.duration_secs || '',
        s.is_voicemail ? 'Yes' : 'No',
        s.cost ? `$${(s.cost / 100).toFixed(2)}` : '',
        (s.summary || '').replace(/"/g, '""')
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `calls_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return <div className="loading">Loading calls data...</div>;
  }

  return (
    <>
      <div className="controls" style={{ marginTop: '20px', flexWrap: 'wrap' }}>
        <div className="control-group">
          <label htmlFor="willingToPay">Willing to Pay</label>
          <select
            id="willingToPay"
            value={filters.willingToPay}
            onChange={(e) => handleFilterChange('willingToPay', e.target.value)}
          >
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="startDate">Start Date</label>
          <input
            type="date"
            id="startDate"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="control-group">
          <label htmlFor="endDate">End Date</label>
          <input
            type="date"
            id="endDate"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        <div className="control-group">
          <label htmlFor="searchTerm">Search</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Name, phone, client..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            style={{ minWidth: '200px' }}
          />
        </div>

        <button onClick={loadCalls}>🔄 Refresh</button>
        <button onClick={exportToCSV} disabled={filteredCalls.length === 0}>
          📥 Export CSV
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Calls</h3>
          <div className="value">{filteredCalls.length}</div>
          <div className="subvalue">of {calls.length} total</div>
        </div>
        <div className="stat-card">
          <h3>Willing to Pay</h3>
          <div className="value profit">
            {filteredCalls.filter(c => c.call_summary?.willing_to_pay).length}
          </div>
          <div className="subvalue">
            {calls.length > 0
              ? ((filteredCalls.filter(c => c.call_summary?.willing_to_pay).length / filteredCalls.length) * 100).toFixed(1)
              : 0}% conversion
          </div>
        </div>
        <div className="stat-card">
          <h3>User Responded</h3>
          <div className="value">
            {filteredCalls.filter(c => c.call_summary?.user_responded).length}
          </div>
          <div className="subvalue">
            {filteredCalls.length > 0
              ? ((filteredCalls.filter(c => c.call_summary?.user_responded).length / filteredCalls.length) * 100).toFixed(1)
              : 0}% response rate
          </div>
        </div>
        <div className="stat-card">
          <h3>Voicemails</h3>
          <div className="value">
            {filteredCalls.filter(c => c.call_summary?.is_voicemail).length}
          </div>
          <div className="subvalue">
            {filteredCalls.length > 0
              ? ((filteredCalls.filter(c => c.call_summary?.is_voicemail).length / filteredCalls.length) * 100).toFixed(1)
              : 0}% of calls
          </div>
        </div>
      </div>

      <div className="table-container">
        <h2>Call Details ({filteredCalls.length} calls)</h2>
        
        {filteredCalls.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
            No calls found matching your filters.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Client</th>
                  <th>Batch</th>
                  <th>Willing to Pay</th>
                  <th>Responded</th>
                  <th>Duration</th>
                  <th>Voicemail</th>
                  <th>Cost</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {filteredCalls.map((call, index) => {
                  const summary = call.call_summary || {};
                  return (
                    <tr key={call.id || index}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(summary.created_at)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <strong>{summary.first_name} {summary.last_name}</strong>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {summary.phone_number}
                      </td>
                      <td>{summary.client_name || 'N/A'}</td>
                      <td>{summary.batch_call_name || 'N/A'}</td>
                      <td>
                        <span className={summary.willing_to_pay ? 'profit' : ''}>
                          {summary.willing_to_pay ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td>
                        {summary.user_responded ? '✓ Yes' : '✗ No'}
                      </td>
                      <td>{formatDuration(summary.duration_secs)}</td>
                      <td>
                        {summary.is_voicemail ? '📞 Yes' : '✗ No'}
                      </td>
                      <td className="cost">
                        {summary.cost ? `$${(summary.cost / 100).toFixed(2)}` : 'N/A'}
                      </td>
                      <td style={{ minWidth: '300px', maxWidth: '500px' }}>
                        <div style={{
                          maxHeight: '80px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          cursor: 'pointer',
                          padding: '8px',
                          background: '#f7fafc',
                          borderRadius: '4px',
                          fontSize: '13px',
                          lineHeight: '1.5'
                        }}
                        onClick={() => setSelectedCall(call)}
                        title="Click to view full summary"
                        >
                          {summary.summary || 'No summary available'}
                        </div>
                        <button 
                          onClick={() => setSelectedCall(call)}
                          style={{
                            marginTop: '8px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}
                        >
                          📋 View Full Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Full Screen Summary Modal */}
      {selectedCall && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setSelectedCall(null)}
        >
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '40px',
            width: '95vw',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '900px',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              borderBottom: '3px solid #667eea',
              paddingBottom: '20px'
            }}>
              <h2 style={{ margin: 0, color: '#2d3748', fontSize: '28px', fontWeight: 'bold' }}>
                📞 Call Details
              </h2>
              <button
                onClick={() => setSelectedCall(null)}
                style={{
                  background: '#f56565',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#e53e3e'}
                onMouseOut={(e) => e.target.style.background = '#f56565'}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: '#f7fafc',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <strong style={{ color: '#667eea', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    👤 Name:
                  </strong>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                    {selectedCall.call_summary?.first_name} {selectedCall.call_summary?.last_name}
                  </div>
                </div>
                <div style={{
                  background: '#f7fafc',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <strong style={{ color: '#667eea', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    📱 Phone:
                  </strong>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                    {selectedCall.call_summary?.phone_number}
                  </div>
                </div>
                <div style={{
                  background: '#f7fafc',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <strong style={{ color: '#667eea', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    📅 Date:
                  </strong>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                    {formatDate(selectedCall.call_summary?.created_at)}
                  </div>
                </div>
                <div style={{
                  background: '#f7fafc',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <strong style={{ color: '#667eea', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    ⏱️ Duration:
                  </strong>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                    {formatDuration(selectedCall.call_summary?.duration_secs)}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                {selectedCall.call_summary?.client_name && (
                  <div style={{
                    background: '#f7fafc',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <strong style={{ color: '#667eea', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                      🏢 Client:
                    </strong>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                      {selectedCall.call_summary.client_name}
                    </div>
                  </div>
                )}
                {selectedCall.call_summary?.batch_call_name && (
                  <div style={{
                    background: '#f7fafc',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <strong style={{ color: '#667eea', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                      📦 Batch:
                    </strong>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                      {selectedCall.call_summary.batch_call_name}
                    </div>
                  </div>
                )}
                <div style={{
                  background: selectedCall.call_summary?.willing_to_pay ? '#c6f6d5' : '#fed7d7',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid ' + (selectedCall.call_summary?.willing_to_pay ? '#9ae6b4' : '#fc8181')
                }}>
                  <strong style={{ color: '#2d3748', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    💰 Willing to Pay:
                  </strong>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: selectedCall.call_summary?.willing_to_pay ? '#22543d' : '#742a2a' }}>
                    {selectedCall.call_summary?.willing_to_pay ? '✓ Yes' : '✗ No'}
                  </div>
                </div>
                <div style={{
                  background: selectedCall.call_summary?.user_responded ? '#c6f6d5' : '#fed7d7',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid ' + (selectedCall.call_summary?.user_responded ? '#9ae6b4' : '#fc8181')
                }}>
                  <strong style={{ color: '#2d3748', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    📞 User Responded:
                  </strong>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: selectedCall.call_summary?.user_responded ? '#22543d' : '#742a2a' }}>
                    {selectedCall.call_summary?.user_responded ? '✓ Yes' : '✗ No'}
                  </div>
                </div>
                {selectedCall.call_summary?.cost && (
                  <div style={{
                    background: '#feebc8',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #fbd38d'
                  }}>
                    <strong style={{ color: '#2d3748', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                      💵 Cost:
                    </strong>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#744210' }}>
                      ${(selectedCall.call_summary.cost / 100).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '30px',
              borderRadius: '12px',
              marginTop: '30px',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}>
              <strong style={{ 
                color: 'white', 
                fontSize: '24px', 
                display: 'block', 
                marginBottom: '20px',
                borderBottom: '2px solid rgba(255,255,255,0.3)',
                paddingBottom: '10px'
              }}>
                📝 Call Summary
              </strong>
              <div style={{
                color: 'white',
                lineHeight: '1.8',
                fontSize: '17px',
                whiteSpace: 'pre-wrap',
                background: 'rgba(255, 255, 255, 0.1)',
                padding: '20px',
                borderRadius: '8px',
                minHeight: '100px'
              }}>
                {selectedCall.call_summary?.summary || 'No summary available'}
              </div>
            </div>

            {selectedCall.realtime_transcription && (
              <div style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                padding: '30px',
                borderRadius: '12px',
                marginTop: '30px',
                boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)'
              }}>
                <strong style={{ 
                  color: 'white', 
                  fontSize: '24px', 
                  display: 'block', 
                  marginBottom: '20px',
                  borderBottom: '2px solid rgba(255,255,255,0.3)',
                  paddingBottom: '10px'
                }}>
                  🗣️ Transcription Summary
                </strong>
                <div style={{
                  color: 'white',
                  lineHeight: '1.8',
                  fontSize: '17px',
                  whiteSpace: 'pre-wrap',
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '20px',
                  borderRadius: '8px',
                  minHeight: '100px'
                }}>
                  {selectedCall.realtime_transcription.transcript_summary || 'No transcription available'}
                </div>
              </div>
            )}

            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <button
                onClick={() => setSelectedCall(null)}
                style={{
                  padding: '15px 40px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '700',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5568d3';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#667eea';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }}
              >
                ✓ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CallsTableView;

